import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Eyebrow, Reveal, SectionShell } from "./Shared";

export const FAQS = [
  {
    q: "What does a pickleball agent do?",
    a: "A pickleball agent handles the business of the sport so the player can focus on competition. At SMG that means contract and opportunity guidance, sponsorship and endorsement negotiations, brand and content development, media strategy, and long-term career planning — all coordinated by one team.",
  },
  {
    q: "How do I get signed by SMG?",
    a: "Use the athlete application in the Get In The Room section — share your playing level, ranking, age range, location and a short bio, and add links to rankings or highlights. The team reviews every submission and reaches out to players who fit the current roster plans.",
  },
  {
    q: "Who does SMG represent?",
    a: "Professional pickleball players, emerging pros, and youth and NIL talent (a parent or legal guardian is required for youth inquiries). The signed roster is public on this page — currently featuring pros such as Etienne Blaszkewycz, William Hsieh, Armaan Jiwa Mawji and Troy Akin.",
  },
  {
    q: "How can brands partner with SMG athletes?",
    a: "Use the brands and partners application in the Get In The Room section. SMG builds sponsorships, endorsements, event partnerships, travel opportunities and co-branded products, matching each brand with the right player and audience.",
  },
  {
    q: "Does SMG work with players who are not professional yet?",
    a: "Yes. Alongside professionals, SMG works with emerging players and youth and NIL talent — guiding development, positioning and qualified opportunities as a career grows. Pickleball first, with the infrastructure to represent athletes across emerging and established sports.",
  },
  {
    q: "Where is SMG Sports Management based?",
    a: "Carlsbad, California. SMG Sports Management Group International is a division of SMG Media Group and represents players internationally — not tied to one region or one tour.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <SectionShell id="faq">
      <Reveal>
        <Eyebrow className="mb-5">— 07 / Straight Answers</Eyebrow>
        <h2 className="max-w-3xl font-display text-4xl uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
          Everything people ask a <span className="text-volt">pickleball agent.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Short answers on representation, getting signed and brand partnerships — for
          players and brands sizing up SMG.
        </p>
      </Reveal>

      <div className="mt-14">
        {FAQS.map((f, i) => (
          <Reveal key={f.q} delay={0.04 * i}>
            <div
              className={`border-t border-white/10 ${i === FAQS.length - 1 ? "border-b" : ""}`}
              data-testid={`faq-item-${i + 1}`}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-question-${i + 1}`}
                aria-expanded={open === i}
                className="group flex w-full items-center justify-between gap-6 px-2 py-7 text-left transition-colors hover:bg-ink/60"
              >
                <span
                  className={`font-display text-lg uppercase leading-snug transition-colors duration-300 sm:text-2xl ${
                    open === i ? "text-volt" : "text-white group-hover:text-volt"
                  }`}
                >
                  {f.q}
                </span>
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                    open === i
                      ? "rotate-45 border-volt bg-volt text-ink"
                      : "border-white/15 text-slate-400 group-hover:border-volt/50 group-hover:text-volt"
                  }`}
                >
                  <Plus className="h-4 w-4" />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <p
                      className="max-w-3xl px-2 pb-8 text-sm leading-relaxed text-slate-300 sm:text-base"
                      data-testid={`faq-answer-${i + 1}`}
                    >
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}
      </div>
    </SectionShell>
  );
}
